import { DOCUMENT } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { AuditLogInput, createAuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { DealerBranding, DealerBrandingListItem } from "@app/entity/model/dealer-branding";
import { DealerBrandingService } from "@app/entity/services/dealer-branding.service";
import { Project } from "@app/project/model";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { ProjectService } from "@app/project/services/project.service";
import { isEmbedded } from "@app/shared/resources/embedded-mobile-checker";
import { MatIconLoaderService } from "@app/shared/services/mat-icon-loader.service";
import { Observable, Subject, map, shareReplay, take } from "rxjs";
import { ClareRxDialogComponent } from "../clare-rx-dialog/clare-rx-dialog.component";
import { ClareProcessStep } from "@app/shared/model/utility.model";
import { MatStepper } from "@angular/material/stepper";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import {
    DEALER_LOCKOUT_UPSERT_IMAGE_ACTION,
    EditLockoutScreenComponent,
} from "@app/entity/components/dealer-branding/edit-lockout-screen/edit-lockout-screen.component";
import { DealerBrandingManagementComponent } from "@app/entity/components/dealer-branding/dealer-branding-management/dealer-branding-management.component";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MatIconRegistry } from "@angular/material/icon";

@Component({
    selector: "app-create-lockout-files",
    templateUrl: "./create-lockout-files.component.html",
    styleUrls: ["./create-lockout-files.component.scss"],
})
export class CreateLockoutFilesComponent implements OnInit {
    @Input() uploadedFileName = "Test";
    @Input() pngKey$: Observable<string>;
    @Input() pngKey = "";
    @Input() entityId: number = null;
    @Input() isProjectView = false;
    @Output() emitImageUrl = new EventEmitter();

    dialogIcons = [
        ["checkmark", "checkmark"],
        ["download", "download"],
        ["exclamation", "exclamation"],
    ];

    unsubscribe$ = new Subject();

    @ViewChild("clareRxDialog", { static: true, read: TemplateRef }) rxDialogComponent: TemplateRef<ClareRxDialogComponent>;
    @ViewChild("clareRxDialogContainer", { static: true, read: ViewContainerRef }) rxDialogContainer: ViewContainerRef;
    @ViewChild("stepper") stepper: MatStepper;
    @ViewChild("selectCreateOrUploadStep") selectCreateOrUploadStep: EditLockoutScreenComponent;
    @ViewChild("dealerBrandingCreate") dealerBrandingCreate: DealerBrandingManagementComponent;

    configData: {
        icon?: string;
        title?: string;
        message?: string;
        messageHint?: string;
        isLoading?: boolean;
        loadingMessage?: string;
        method?: () => void;
        buttonText?: string;
    };
    image: DealerBranding;
    showCreation = false;
    imageUrl: SafeUrl;

    public steps: ClareProcessStep[] = [
        {
            id: 0,
            text: "Choose to Create or Upload",
            isCompleted: false,
        },
        {
            id: 1,
            text: "Upload/Creation Process",
            isCompleted: false,
        },
        {
            id: 2,
            text: "Name Your File and Download It",
            isCompleted: false,
        },
    ];
    public dealerLockoutUpsertImageAction = DEALER_LOCKOUT_UPSERT_IMAGE_ACTION;
    public lockoutNameForm: FormGroup = this.formBuilder.group({ name: ["", [this.validateFilenameNotInUse(), Validators.required]] });
    public step1Value: DEALER_LOCKOUT_UPSERT_IMAGE_ACTION;
    private currentStepIndex = 0;
    private selectedFile: DealerBranding | null = null;
    private project: Project;
    private existingImages: DealerBrandingListItem[];

    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private readonly dealerBrandingService: DealerBrandingService,
        private readonly dialog: MatDialog,
        private readonly sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
        private projectService: ProjectService,
        readonly matIconLoader: MatIconLoaderService,
        private readonly formBuilder: FormBuilder,
        private readonly iconRegistry: MatIconRegistry
    ) {
        matIconLoader.importIcons(this.dialogIcons);
        this.iconRegistry.addSvgIcon(
            "download_no_border",
            this.sanitizer.bypassSecurityTrustResourceUrl("assets/images/download-no-border.svg")
        );
    }

    ngOnInit(): void {
        if (this.isProjectView) {
            this.projectService
                .getActiveProject$()
                .pipe(
                    shareReplay(1),
                    take(1),
                    map(activeProj => {
                        this.project = activeProj;
                    })
                )
                .subscribe();
        }

        this.dealerBrandingService
            .loadDealerBrandingImageList$(this.entityId)
            .pipe(take(1))
            .subscribe(images => {
                this.existingImages = images.result;
            });
    }

    /**
     * @returns {boolean} Returns true if is embedded, false otherwise
     */
    get isEmbedded() {
        return isEmbedded();
    }

    onUpsertImageAction(action: DEALER_LOCKOUT_UPSERT_IMAGE_ACTION) {
        this.configData = {};
        if (action === DEALER_LOCKOUT_UPSERT_IMAGE_ACTION.UPLOAD) {
            this.displaySaveImageDialog();
        } else if (action === DEALER_LOCKOUT_UPSERT_IMAGE_ACTION.CREATE) {
            this.openDealerBrandingManagement();
        }
    }

    onSaveImagePNG(image) {
        this.selectedFile = image;
        this.cdr.detectChanges();
    }

    /**
     *Displays override or upload dialog depending on image value
     *
     * @param configData
     * @returns {*} Opens override dialog if image already available, runs upload if not
     * @memberof DealerBrandingContainerComponent
     */
    displaySaveImageDialog() {
        this.configData = {};
        this.runImageUpload();
    }

    openDealerBrandingManagement() {
        this.configData = {};
        const runDealerBrandingManagement = () => {
            this.rxDialogContainer.clear();
            this.dialog.closeAll();
            this.showCreation = true;
            this.cdr.detectChanges();
        };

        runDealerBrandingManagement();
    }

    /**
     *Sets properties for Override Dialog and runs displayConfig() to execute popover
     *
     * @param configData
     * @memberof DealerBrandingContainerComponent
     */
    displayOverrideDialog() {
        this.configData.icon = "exclamation";
        this.configData.title = $localize`Panel Lockout Screen previously created`;
        this.configData.message = $localize`Are you sure you want to proceed and override previous changes?`;
        this.configData.buttonText = $localize`:@@SHARED.BTN.CONTINUE:Continue`;
        this.openRxDialog();
    }

    /**
     *Uploads user image file
     *
     * @param configData
     * @memberof DealerBrandingContainerComponent
     */
    private runImageUpload() {
        this.selectedFile = null;
        this.configData.title = $localize`Uploading Image`;
        this.configData.isLoading = true;
        const imageInput = this._document.createElement("input");
        imageInput.type = "file";
        imageInput.hidden = true;
        imageInput.accept = "image/png, image/jpg, image/jpeg";
        imageInput.addEventListener("change", _ => {
            const newImg = imageInput.files[0];
            if (newImg) {
                this.resizeImg(newImg);
            }
        });
        imageInput.addEventListener("click", _ => {
            this.dialog.closeAll();
        });
        imageInput.click();
    }

    private resizeImg(uploadedImg: File) {
        const canvas = this._document.createElement("canvas");
        canvas.id = "uploadImg";
        canvas.width = 1024;
        canvas.height = 600;

        const img = new Image();
        img.crossOrigin = "Anonymous";

        const reader = new FileReader();
        reader.onload = () => {
            img.onload = () => {
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(blob => {
                    const image: DealerBranding = {
                        key: this.pngKey,
                        imageFile: blob,
                    };
                    this.selectedFile = image;
                    this.cdr.detectChanges();
                });
            };
            img.src = reader.result.toString();
        };
        reader.readAsDataURL(uploadedImg);
    }

    upsertNewImage() {
        const didCreate = this.step1Value === DEALER_LOCKOUT_UPSERT_IMAGE_ACTION.CREATE;
        const logData = this.getUploadLogData(didCreate);
        const callbackEmitter = new EventEmitter();

        callbackEmitter.pipe(take(1)).subscribe(_ => {
            this.dealerBrandingService.goToDealerBranding(this.entityId);
        });

        this.dealerBrandingService.createDealerBrandingImage(
            this.entityId,
            this.lockoutNameForm.get("name")?.value,
            this.selectedFile.imageFile,
            callbackEmitter,
            logData
        );
    }

    getUploadLogData(isCreated = false): AuditLogInput {
        if (this.project) {
            const auditName = createAuditLogKey(
                LOG_CATEGORY.ACCOUNT,
                LOG_TYPE.CLAREONE,
                isCreated ? LOG_SUB_TYPE.DEALER_LOCKOUT_CREATE : LOG_SUB_TYPE.DEALER_LOCKOUT_UPLOAD
            );
            const data = isCreated ? "Lockout Created" : "Lockout Uploaded";
            const uuid = ProjectHelperService.getProjectUuid(this.project);
            return createAuditLogInput(auditName, data, this.project, { uuid });
        }
        return null;
    }

    /**
     *Sets properties for upload success dialog and runs displayConfig() to execute popover
     *
     * @param willCreate
     * @param didCreate
     * @memberof DealerBrandingContainerComponent
     */
    private displayUploadSuccessDialog(didCreate = false) {
        this.showCreation = false;
        this.configData = {};
        this.configData.icon = "checkmark";
        this.configData.title = didCreate
            ? $localize`Custom Screen successfully created`
            : $localize`Your Own Lockout Panel Screen was successfully uploaded`;
        this.configData.isLoading = false;
        this.cdr.detectChanges();
    }

    cancel() {
        this.showCreation = false;
        this.cdr.detectChanges();
    }

    private openRxDialog() {
        this.dialog.closeAll();
        this.dialog.open(this.rxDialogComponent, { maxWidth: 500 });
    }

    /**
     * Creates a browser-accepted url for user to download
     *
     * @param  {Blob} image File to be converted to url for download
     * @returns {SafeUrl} User downloads file in form of url
     */
    createImageUrl(image: Blob): SafeUrl {
        return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(image));
    }

    public onTabSelectionChange(tabChange: StepperSelectionEvent) {
        this.currentStepIndex = tabChange.selectedIndex;
        this.steps.forEach(step => {
            step.isActive = step.id === this.currentStepIndex;
            if (step.id === this.currentStepIndex) {
                step.isTouched = true;
            }
        });
    }

    public isStep2Valid(): boolean {
        return (
            (this.step1Value === DEALER_LOCKOUT_UPSERT_IMAGE_ACTION.UPLOAD && !!this.selectedFile) ||
            (this.step1Value === DEALER_LOCKOUT_UPSERT_IMAGE_ACTION.CREATE && !!this.dealerBrandingCreate?.createdImagePNG)
        );
    }

    public onStep2Next(): void {
        if (this.step1Value === DEALER_LOCKOUT_UPSERT_IMAGE_ACTION.CREATE) {
            this.dealerBrandingCreate.createImage();
        }
        this.stepper.next();
    }

    public downloadLocalImage(): void {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(this.selectedFile.imageFile);
        link.download = this.dealerBrandingService.convertNameToImagePath(this.lockoutNameForm.controls.name.value as string);

        link.click();

        URL.revokeObjectURL(link.href);
    }

    private validateFilenameNotInUse(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const inputValue = control.value?.trim();

            if (!inputValue) {
                return null;
            }

            const isDuplicate = this.existingImages.some(item => item.filename === inputValue);

            return isDuplicate ? { filenameInUse: "This name is already in use for an existing lockout image." } : null;
        };
    }
}
